/**
 * Icelandic fallback for Intl date formatting. Safari (iPad/iPhone) ships full Icelandic ICU data,
 * but some Chromium builds and older Android WebViews do not — there `new Intl.DateTimeFormat('is-IS')`
 * silently falls back to English. These factories return the real Intl formatter when Icelandic is
 * supported and a small hand-written formatter otherwise (covers the option sets the app uses).
 * Reykjavík = UTC, so UTC getters give local wall-clock time.
 */

const MONTHS_LONG = ['janúar', 'febrúar', 'mars', 'apríl', 'maí', 'júní', 'júlí', 'ágúst', 'september', 'október', 'nóvember', 'desember'];
const MONTHS_SHORT = ['jan.', 'feb.', 'mar.', 'apr.', 'maí', 'jún.', 'júl.', 'ágú.', 'sep.', 'okt.', 'nóv.', 'des.'];
// Monday-first (ISO 1..7)
const WEEKDAYS_LONG = ['mánudagur', 'þriðjudagur', 'miðvikudagur', 'fimmtudagur', 'föstudagur', 'laugardagur', 'sunnudagur'];
const WEEKDAYS_SHORT = ['mán.', 'þri.', 'mið.', 'fim.', 'fös.', 'lau.', 'sun.'];
const WEEKDAYS_NARROW = ['M', 'Þ', 'M', 'F', 'F', 'L', 'S'];

let supported: boolean | null = null;
export function icuHasIcelandic(): boolean {
  if (supported === null) {
    try {
      supported =
        Intl.DateTimeFormat.supportedLocalesOf(['is']).length > 0 &&
        new Intl.DateTimeFormat('is-IS', { month: 'long', timeZone: 'UTC' }).format(Date.UTC(2026, 9, 1)) === 'október';
    } catch {
      supported = false;
    }
  }
  return supported;
}

const isIcelandic = (loc: string) => loc.toLowerCase().startsWith('is');

export interface DateFormatterLike {
  format(ms: number): string;
  formatToParts(ms: number): Intl.DateTimeFormatPart[];
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function icelandicParts(ms: number, o: Intl.DateTimeFormatOptions): Intl.DateTimeFormatPart[] {
  const d = new Date(ms);
  const parts: Intl.DateTimeFormatPart[] = [];
  const lit = (value: string) => parts.push({ type: 'literal', value });
  const hasDate = !!(o.day || o.month || o.year);
  if (o.weekday) {
    const i = (d.getUTCDay() + 6) % 7;
    const names = o.weekday === 'long' ? WEEKDAYS_LONG : o.weekday === 'narrow' ? WEEKDAYS_NARROW : WEEKDAYS_SHORT;
    parts.push({ type: 'weekday', value: names[i]! });
    if (hasDate || o.hour) lit(', ');
  }
  if (o.day) {
    parts.push({ type: 'day', value: o.day === '2-digit' ? pad2(d.getUTCDate()) : String(d.getUTCDate()) });
    lit(o.month ? '. ' : '.');
  }
  if (o.month) {
    const m = d.getUTCMonth();
    const value =
      o.month === 'long' ? MONTHS_LONG[m]! : o.month === 'short' ? MONTHS_SHORT[m]! : o.month === '2-digit' ? pad2(m + 1) : String(m + 1);
    parts.push({ type: 'month', value });
    if (o.year) lit(' ');
  }
  if (o.year) parts.push({ type: 'year', value: String(d.getUTCFullYear()) });
  if (o.hour) {
    if (hasDate || o.weekday) lit(' kl. ');
    parts.push({ type: 'hour', value: pad2(d.getUTCHours()) });
    if (o.minute) {
      lit(':');
      parts.push({ type: 'minute', value: pad2(d.getUTCMinutes()) });
    }
  }
  return parts;
}

/** Drop-in for `new Intl.DateTimeFormat(loc, opts)` with an Icelandic fallback. */
export function makeDateTimeFormat(loc: string, opts: Intl.DateTimeFormatOptions): DateFormatterLike {
  if (isIcelandic(loc) && !icuHasIcelandic()) {
    return {
      format: (ms) => icelandicParts(ms, opts).map((p) => p.value).join(''),
      formatToParts: (ms) => icelandicParts(ms, opts),
    };
  }
  return new Intl.DateTimeFormat(loc, opts);
}

export interface RelativeFormatterLike {
  format(value: number, unit: Intl.RelativeTimeFormatUnit): string;
}

/** Icelandic CLDR plural "one": n % 10 = 1 and n % 100 ≠ 11. */
function isOne(n: number): boolean {
  return n % 10 === 1 && n % 100 !== 11;
}

const REL_UNITS: Record<string, { fut: [string, string]; past: [string, string]; zero: string }> = {
  second: { fut: ['sekúndu', 'sekúndur'], past: ['sekúndu', 'sekúndum'], zero: 'núna' },
  minute: { fut: ['mínútu', 'mínútur'], past: ['mínútu', 'mínútum'], zero: 'á þessari mínútu' },
  hour: { fut: ['klukkustund', 'klukkustundir'], past: ['klukkustund', 'klukkustundum'], zero: 'þessa stundina' },
  day: { fut: ['dag', 'daga'], past: ['degi', 'dögum'], zero: 'í dag' },
  week: { fut: ['viku', 'vikur'], past: ['viku', 'vikum'], zero: 'í þessari viku' },
  month: { fut: ['mánuð', 'mánuði'], past: ['mánuði', 'mánuðum'], zero: 'í þessum mánuði' },
  year: { fut: ['ár', 'ár'], past: ['ári', 'árum'], zero: 'á þessu ári' },
};

function icelandicRelative(value: number, unit: Intl.RelativeTimeFormatUnit): string {
  const u = String(unit).replace(/s$/, '');
  if (u === 'day') {
    if (value === -1) return 'í gær';
    if (value === 1) return 'á morgun';
    if (value === -2) return 'í fyrradag';
    if (value === 2) return 'eftir tvo daga';
  }
  const t = REL_UNITS[u] ?? REL_UNITS.day!;
  if (value === 0) return t.zero;
  const n = Math.abs(value);
  const one = isOne(n);
  return value > 0 ? `eftir ${n} ${one ? t.fut[0] : t.fut[1]}` : `fyrir ${n} ${one ? t.past[0] : t.past[1]}`;
}

/** Drop-in for `new Intl.RelativeTimeFormat(loc, { numeric: 'auto' })` with an Icelandic fallback. */
export function makeRelativeTimeFormat(loc: string): RelativeFormatterLike {
  if (isIcelandic(loc) && !icuHasIcelandic()) return { format: icelandicRelative };
  return new Intl.RelativeTimeFormat(loc, { numeric: 'auto' });
}
